import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiChevronsLeft, FiChevronsRight, FiChevronDown, FiLogOut } from 'react-icons/fi';
import { SidebarData } from './SidebarData';
import Logo from '../../../section/Header/Logo';
import './SideBar.css';

const API = process.env.REACT_APP_API_URL;

const isGroupActive = (item, pathname) =>
  item.children ? item.children.some((c) => c.link === pathname) : item.link === pathname;

const Sidebar = ({ mobileOpen, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openTitles, setOpenTitles] = useState(() =>
    SidebarData.flatMap((g) => g.items).filter((i) => isGroupActive(i, location.pathname)).map((i) => i.titre)
  );
  const [connectedUser, setConnectedUser] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('admin_data');
    if (raw) setConnectedUser(JSON.parse(raw));
  }, []);

  useEffect(() => {
    const activeParent = SidebarData.flatMap((g) => g.items).find((i) => isGroupActive(i, location.pathname));
    if (activeParent && !openTitles.includes(activeParent.titre)) {
      setOpenTitles((prev) => [...prev, activeParent.titre]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (titre) => {
    setOpenTitles((prev) => (prev.includes(titre) ? prev.filter((t) => t !== titre) : [...prev, titre]));
  };

  const [badges, setBadges] = useState({});

  useEffect(() => {
    const authorization = localStorage.getItem('token');
    if (!authorization) return;
    const headers = { Authorization: `Bearer ${authorization}` };
    Promise.all([
      axios.get(`${API}/commande/lister`, { headers }).then((res) => res.data).catch(() => []),
      axios.get(`${API}/contact/lister`, { headers }).then((res) => res.data).catch(() => []),
    ]).then(([commandes, reclamations]) => {
      setBadges({
        '/admin/Commande': commandes.filter((c) => (c.statut || 'En attente') === 'En attente').length,
        '/admin/Reclamation': reclamations.filter((r) => !r.lu).length,
      });
    });
  }, []);

  const handleNavigate = (link) => {
    navigate(link);
    onCloseMobile();
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const initials = connectedUser
    ? `${connectedUser.prenom?.[0] || ''}${connectedUser.nom?.[0] || ''}`.toUpperCase() || 'A'
    : 'A';

  return (
    <>
      {mobileOpen && (
        <div className="hd-admin-sidebar-backdrop" onClick={onCloseMobile} />
      )}

      <aside className={`hd-admin-sidebar ${isCollapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'is-mobile-open' : ''}`}>
        <div className="hd-admin-sidebar-brand">
          <Logo compact={isCollapsed} />
          <button
            type="button"
            className="hd-admin-sidebar-collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Agrandir' : 'Réduire'}
          >
            {isCollapsed ? <FiChevronsRight size={16} /> : <FiChevronsLeft size={16} />}
          </button>
        </div>

        <nav className="hd-admin-sidebar-nav">
          {SidebarData.map((group) => (
            <div className="hd-admin-sidebar-group" key={group.section}>
              {!isCollapsed && (
                <span className="hd-admin-sidebar-group-label">{group.section}</span>
              )}
              <ul className="hd-admin-sidebar-list">
                {group.items.map((item) => {
                  if (item.children) {
                    const active = isGroupActive(item, location.pathname);
                    const open = openTitles.includes(item.titre);
                    return (
                      <li key={item.titre}>
                        <button
                          type="button"
                          className={`hd-admin-sidebar-item ${active ? 'is-active' : ''}`}
                          onClick={() => (isCollapsed ? handleNavigate(item.children[0].link) : toggleGroup(item.titre))}
                          title={isCollapsed ? item.titre : undefined}
                        >
                          <span className="hd-admin-sidebar-icon">{item.icon}</span>
                          {!isCollapsed && (
                            <>
                              <span className="hd-admin-sidebar-title">{item.titre}</span>
                              <FiChevronDown className={`hd-admin-sidebar-chevron ${open ? 'is-open' : ''}`} />
                            </>
                          )}
                        </button>

                        <AnimatePresence initial={false}>
                          {!isCollapsed && open && (
                            <motion.ul
                              className="hd-admin-sidebar-sublist"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.22, ease: 'easeOut' }}
                            >
                              {item.children.map((child) => (
                                <li key={child.link}>
                                  <button
                                    type="button"
                                    className={`hd-admin-sidebar-subitem ${location.pathname === child.link ? 'is-active' : ''}`}
                                    onClick={() => handleNavigate(child.link)}
                                  >
                                    {child.titre}
                                  </button>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    );
                  }

                  const active = location.pathname === item.link;
                  return (
                    <li key={item.link}>
                      <button
                        type="button"
                        className={`hd-admin-sidebar-item ${active ? 'is-active' : ''}`}
                        onClick={() => handleNavigate(item.link)}
                        title={isCollapsed ? item.titre : undefined}
                      >
                        <span className="hd-admin-sidebar-icon">{item.icon}</span>
                        {!isCollapsed && <span className="hd-admin-sidebar-title">{item.titre}</span>}
                        {!isCollapsed && !!badges[item.link] && (
                          <span className="hd-admin-sidebar-badge">{badges[item.link]}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="hd-admin-sidebar-footer">
          <div className="hd-admin-sidebar-footer-avatar">{initials}</div>
          {!isCollapsed && (
            <div className="hd-admin-sidebar-footer-info">
              <p className="hd-admin-sidebar-footer-name">
                {connectedUser ? `${connectedUser.prenom} ${connectedUser.nom}` : 'Administrateur'}
              </p>
              <p className="hd-admin-sidebar-footer-role">
                {connectedUser?.superAdmin ? 'Super Admin' : 'Administrateur'}
              </p>
            </div>
          )}
          <button type="button" className="hd-admin-sidebar-footer-logout" onClick={handleLogout} title="Déconnexion">
            <FiLogOut size={16} />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
